import { useMemo, useState } from 'react';
import { cn } from 'cn';
import { useQuery } from '@tanstack/react-query';
import { DropdownMenu } from 'radix-ui';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { IconRail } from '@/features/reader/IconRail';
import {
  fetchKhutbahs,
  fetchKhutbahsByIds,
  type KhutbahCard,
} from '@/features/home/homeApi';
import { clearLibrary } from './libraryStore';
import { useHistory, useSaved } from './useLibrary';

/**
 * The reader's library: what the publishers have, what this reader kept, and
 * what they have been reading.
 *
 * From the Library-and-Settings mock. Two of its rules are load-bearing:
 *
 * - **"Honesty about accounts."** The header says "This library lives in this
 *   browser", because it does: saved items and history are ids in
 *   `localStorage` and there is nowhere else for them to go while brief §18
 *   is open.
 * - **"Private is legible three ways, none of them colour alone."** Every row
 *   carries its source badge and a visibility mark in words, so a shared
 *   khutbah and a private one are never told apart by a tint.
 *
 * **"Mine" is empty, and says why rather than pretending.** A reader's own
 * uploads are Phase 6 and there is no endpoint behind them — not an empty
 * list, an absent feature. Rendering an empty list would claim the feature
 * works and this reader simply has nothing, which is a different and untrue
 * statement. The upload control on this screen goes to the editor upload,
 * which is the publisher path, and is labelled as such.
 */

type Filter = 'all' | 'saved' | 'history' | 'mine';

const FILTERS: readonly { id: Filter; label: string }[] = [
  { id: 'all', label: 'All khutbahs' },
  { id: 'saved', label: 'Saved' },
  { id: 'history', label: 'Recently read' },
  { id: 'mine', label: 'My uploads' },
];

function formatFriday(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function relative(iso: string, now: Date): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? 'a week ago' : `${weeks} weeks ago`;
}

/**
 * The Library's overflow menu.
 *
 * Two actions, and they are deliberately different kinds of thing: one opens
 * preparation, the other destroys local data. "Clear saved and history"
 * appears only when there is something to clear — an enabled control that does
 * nothing is a worse answer than an absent one — and it is marked as
 * destructive in words and in colour, never colour alone.
 *
 * Radix's DropdownMenu rather than a hand-rolled popover: it comes with the
 * roving focus, typeahead, Escape handling and `aria-activedescendant` that a
 * menu needs, and `radix-ui` is already a dependency for the language select.
 * The alternative was a third partial implementation of keyboard menus.
 */
function LibraryMenu({ hasLocalEntries }: { hasLocalEntries: boolean }) {
  const item =
    'flex min-h-9 cursor-pointer select-none items-center rounded-md px-2 py-2 text-sm ' +
    'outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground';

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button type="button" variant="outline" size="lg">
          More
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="size-4"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-60 rounded-lg border border-border bg-popover p-1 shadow-2"
        >
          <DropdownMenu.Item className={item} asChild>
            <a href="?screen=prepare">
              <span className="flex flex-col items-start">
                <span>Prepare from published khutbahs</span>
                {/*
                  The label has to carry the limit. "Create a khutbah" would
                  promise that something writes one, and nothing here does:
                  this searches what the ministries published and hands back
                  their own passages with citations.
                */}
                <span className="text-[11px] text-muted-foreground">
                  Find passages on a topic, with citations
                </span>
              </span>
            </a>
          </DropdownMenu.Item>

          <DropdownMenu.Item className={item} asChild>
            <a href="?screen=chat">
              <span className="flex flex-col items-start">
                <span>Ask about a khutbah</span>
                <span className="text-[11px] text-muted-foreground">
                  Answers come only from the khutbah you pick
                </span>
              </span>
            </a>
          </DropdownMenu.Item>

          {hasLocalEntries ? (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                className={cn(item, 'text-destructive')}
                onSelect={() => clearLibrary()}
              >
                Clear saved and history
              </DropdownMenu.Item>
            </>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function LibraryRow({
  card,
  readAt,
  saved,
}: {
  card: KhutbahCard;
  readAt: string | null;
  saved: boolean;
}) {
  return (
    <li className="border-b border-border last:border-b-0">
      <a
        href={`?khutbah=${encodeURIComponent(card.khutbahId)}`}
        className="flex flex-col gap-2 px-1 py-4 hover:bg-muted/40"
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span>{formatFriday(card.friday)}</span>
          <span aria-hidden>·</span>
          <span>{card.institutionName}</span>
          {/*
            Visibility in words, never a tint alone. Everything reachable
            here is published, so every row says so — the mark is not
            decoration, it is the statement that this is not private.
          */}
          <span aria-hidden>·</span>
          <span>
            {card.sourceKind === 'publisher'
              ? 'Publisher-sourced · shared with everyone'
              : 'User-submitted'}
          </span>
          {readAt ? (
            <>
              <span aria-hidden>·</span>
              <span>read {readAt}</span>
            </>
          ) : null}
          {saved ? (
            <>
              <span aria-hidden>·</span>
              <span>Saved</span>
            </>
          ) : null}
        </div>

        {/* Translated first, Arabic beneath: a list is scanned to choose,
            and it is scanned in the reader's own language. */}
        {card.titleTranslated ? (
          <p className="text-base font-medium text-pretty">{card.titleTranslated}</p>
        ) : null}

        {card.titleOriginal ? (
          <p
            lang="ar"
            dir="rtl"
            className={cn(
              'script-ar text-reading-foreground',
              card.titleTranslated ? 'text-sm text-muted-foreground' : 'text-lg',
            )}
          >
            {card.titleOriginal}
          </p>
        ) : card.titleTranslated ? null : (
          <p className="text-base font-medium">Khutbah of {formatFriday(card.friday)}</p>
        )}

        {card.languages.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5">
            {card.languages.map((language) => (
              <li
                key={language.code}
                className={cn(
                  'rounded-full border px-2 py-0.5 text-[11px]',
                  language.state === 'reviewed'
                    ? 'border-success bg-success-bg text-success'
                    : 'border-dashed border-border text-muted-foreground',
                )}
              >
                {language.name}
                {' · '}
                {language.state === 'reviewed' ? 'reviewed' : 'unreviewed'}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">No translation yet.</p>
        )}
      </a>
    </li>
  );
}

export function LibraryScreen() {
  const [filter, setFilter] = useState<Filter>('all');
  const history = useHistory();
  const saved = useSaved();
  const now = new Date();

  // One request for the catalogue; the filters are applied to it here
  // because saved and history are ids this browser holds and the server
  // knows nothing about them.
  const khutbahs = useQuery({
    queryKey: ['khutbahs', 'library'],
    queryFn: ({ signal }) => fetchKhutbahs({ limit: 50 }, signal),
    staleTime: 5 * 60 * 1000,
  });

  // Saved and history are ids this browser holds and the server knows
  // nothing about. They used to be resolved by intersecting them with the
  // newest 50 khutbahs, so anything older simply vanished: the sidebar
  // counted a saved khutbah and the body said "Nothing saved yet".
  //
  // They are fetched by id instead, gated server-side exactly as a browse
  // is — knowing an id is not a way past the publication check.
  const keptIds = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of history) ids.add(entry.khutbahId);
    for (const id of saved) ids.add(id);
    return [...ids];
  }, [history, saved]);

  const kept = useQuery({
    queryKey: ['khutbahs', 'by-id', [...keptIds].sort()],
    queryFn: ({ signal }) => fetchKhutbahsByIds(keptIds, signal),
    enabled: keptIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // The catalogue page, plus anything kept that was not on it. Keyed by id
  // so a khutbah appearing in both is one row, not two.
  const all = useMemo(() => {
    const byId = new Map<string, KhutbahCard>();
    for (const card of khutbahs.data ?? []) byId.set(card.khutbahId, card);
    for (const card of kept.data ?? []) byId.set(card.khutbahId, card);
    return [...byId.values()].sort((a, b) => b.friday.localeCompare(a.friday));
  }, [khutbahs.data, kept.data]);
  const readAtOf = new Map(history.map((entry) => [entry.khutbahId, entry.readAt]));

  let shown: KhutbahCard[] = all;
  if (filter === 'saved') {
    shown = all.filter((card) => saved.includes(card.khutbahId));
  } else if (filter === 'history') {
    // History order, not catalogue order: the question is "what was I
    // reading?", and the answer is a sequence.
    shown = history
      .map((entry) => all.find((card) => card.khutbahId === entry.khutbahId))
      .filter((card): card is KhutbahCard => card !== undefined);
  } else if (filter === 'mine') {
    shown = [];
  }

  const counts: Record<Filter, number | null> = {
    all: all.length,
    saved: saved.length,
    history: history.length,
    mine: null,
  };

  return (
    <div className="flex h-[calc(100dvh-var(--app-bar-height))] overflow-hidden">
      <IconRail />

      {/* The mock's SidebarLibrary: the filters, and the honest line at its foot. */}
      <nav
        aria-label="Library"
        className="hidden w-56 shrink-0 flex-col justify-between border-e border-sidebar-border bg-sidebar px-2 py-4 lg:flex"
      >
        <div className="flex flex-col gap-1">
          <p className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Library
          </p>
          {FILTERS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-current={entry.id === filter ? 'true' : undefined}
              onClick={() => setFilter(entry.id)}
              className={cn(
                'flex min-h-9 items-center justify-between rounded-md px-2 py-2 text-start text-sm',
                'hover:bg-sidebar-accent',
                entry.id === filter
                  ? 'bg-sidebar-accent text-sidebar-primary'
                  : 'text-foreground',
              )}
            >
              <span>{entry.label}</span>
              {counts[entry.id] !== null ? (
                <span className="text-[11px] text-muted-foreground">
                  {counts[entry.id]}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        <p className="px-2 text-[11px] text-muted-foreground">
          This library lives in this browser. Saved khutbahs and your reading
          history are not sent anywhere, and clearing the browser clears them.
        </p>
      </nav>

      <main className="flex min-h-0 flex-col flex-1 overflow-y-auto px-4 py-8 sm:px-6">
        {/* Start-aligned, not centred: the same reasoning as the reader —
            a capped column floating in the middle of a wide pane reads as
            having drifted away from the rail it belongs to. `me-auto`, so
            an RTL interface starts it on the right. */}
        <div className="me-auto w-full max-w-4xl">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">My library</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Published khutbahs, and what you have kept on this device.
              </p>
            </div>
            <LibraryMenu
              hasLocalEntries={saved.length > 0 || history.length > 0}
            />
          </div>

          {/* The filters again, for the widths where the rail is not shown. */}
          <div
            role="group"
            aria-label="Library filter"
            className="mt-4 flex flex-wrap gap-1.5 lg:hidden"
          >
            {FILTERS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                aria-pressed={entry.id === filter}
                onClick={() => setFilter(entry.id)}
                className={cn(
                  'min-h-9 rounded-md border px-3 text-sm',
                  entry.id === filter
                    ? 'border-primary bg-secondary text-secondary-foreground'
                    : 'border-input text-muted-foreground',
                )}
              >
                {entry.label}
              </button>
            ))}
          </div>

          <div className="mt-6">
            {filter === 'mine' ? (
              // Absent, not empty. See the file comment: an empty list would
              // say the feature works and this reader has nothing.
              <div className="rounded-lg border border-dashed border-border p-6">
                <h2 className="text-base font-semibold">
                  Uploading your own khutbah is not built yet
                </h2>
                <p className="mt-2 text-sm text-pretty text-muted-foreground">
                  Private uploads — your own khutbah, visible only to you, with a
                  quota and a deletion grace period — are a later phase. Nothing
                  on this screen is holding them back; the feature does not exist
                  yet.
                </p>
                <p className="mt-2 text-sm text-pretty text-muted-foreground">
                  The upload that does exist is the publisher one, which adds a
                  khutbah for everyone and is part of the editor tools.
                </p>
                <div className="mt-3">
                  <a
                    href="?screen=upload"
                    className="text-sm text-primary underline"
                  >
                    Open the publisher upload
                  </a>
                </div>
              </div>
            ) : khutbahs.isPending ? (
              <div className="flex flex-col gap-4" aria-busy>
                {[0, 1, 2].map((row) => (
                  <div key={row} className="flex flex-col gap-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-6 w-80" />
                  </div>
                ))}
              </div>
            ) : khutbahs.isError ? (
              <div className="rounded-lg border border-border bg-card p-6">
                <h2 className="text-base font-semibold">Could not load the library</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {khutbahs.error instanceof Error
                    ? khutbahs.error.message
                    : 'Something went wrong reaching the server.'}
                </p>
              </div>
            ) : shown.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6">
                <p className="text-sm text-muted-foreground">
                  {filter === 'saved'
                    ? 'Nothing saved yet. Open a khutbah and use Save to keep it here.'
                    : filter === 'history'
                      ? 'Nothing read yet on this device.'
                      : 'No khutbahs have been published yet.'}
                </p>
              </div>
            ) : (
              <ul className="rounded-lg border border-border px-4">
                {shown.map((card) => (
                  <LibraryRow
                    key={card.khutbahId}
                    card={card}
                    readAt={
                      readAtOf.has(card.khutbahId)
                        ? relative(readAtOf.get(card.khutbahId) ?? '', now)
                        : null
                    }
                    saved={saved.includes(card.khutbahId)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
